"use client";

import { useEffect, useMemo, useState } from "react";
import { type CmsHeroSlide } from "@/lib/cms-content";
import { fixMaybeBrokenText } from "@/lib/text-client";

type CmsHeroSliderProps = {
  slides: CmsHeroSlide[];
};

const HERO_PLACEHOLDER_TEXT =
  "Подберите нужный раздел, быстро найдите товар по поиску и оформите заказ без лишних шагов.";

function sanitizeHeroText(value: string | null | undefined) {
  const text = fixMaybeBrokenText(value ?? "").trim();

  if (!text) {
    return "";
  }

  if (/редактируемый hero-блок|здесь можно показывать/i.test(text)) {
    return HERO_PLACEHOLDER_TEXT;
  }

  return text;
}

function sanitizeHeroTitle(value: string | null | undefined) {
  const title = fixMaybeBrokenText(value ?? "").trim();

  if (!title) {
    return "";
  }

  if (/премиальный каталог/i.test(title)) {
    return "Каталог товаров";
  }

  return title;
}

export function CmsHeroSlider({ slides }: CmsHeroSliderProps) {
  const activeSlides = useMemo(
    () =>
      slides
        .filter((slide) => slide.isActive)
        .map((slide) => ({
          ...slide,
          badge: fixMaybeBrokenText(slide.badge).trim(),
          title: sanitizeHeroTitle(slide.title),
          text: sanitizeHeroText(slide.text),
          primaryButtonLabel: fixMaybeBrokenText(slide.primaryButtonLabel).trim(),
          secondaryButtonLabel: fixMaybeBrokenText(slide.secondaryButtonLabel).trim(),
          imageAlt: fixMaybeBrokenText(slide.imageAlt).trim()
        }))
        .filter((slide) => slide.title || slide.text || slide.imageUrl),
    [slides]
  );
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (activeSlides.length <= 1) {
      return;
    }

    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % activeSlides.length);
    }, 6500);

    return () => window.clearInterval(timer);
  }, [activeSlides.length]);

  useEffect(() => {
    if (index >= activeSlides.length) {
      setIndex(0);
    }
  }, [activeSlides.length, index]);

  if (!activeSlides.length) {
    return null;
  }

  const current = activeSlides[index] ?? activeSlides[0];

  return (
    <section className="home-hero-cms">
      <div className="container">
        <article className="home-hero-cms-card">
          <div className="home-hero-cms-copy">
            {current.badge ? <span className="mini-badge">{current.badge}</span> : null}
            <h1 className="home-hero-cms-title">{current.title}</h1>
            {current.text ? <p className="home-hero-cms-text">{current.text}</p> : null}

            <div className="home-hero-cms-actions">
              {current.primaryButtonLabel && current.primaryButtonHref ? (
                <a className="primary-button" href={current.primaryButtonHref}>
                  {current.primaryButtonLabel}
                </a>
              ) : null}

              {current.secondaryButtonLabel && current.secondaryButtonHref ? (
                <a className="secondary-button" href={current.secondaryButtonHref}>
                  {current.secondaryButtonLabel}
                </a>
              ) : null}
            </div>

            {activeSlides.length > 1 ? (
              <div className="home-hero-cms-dots" aria-label="Переключение слайдов главного баннера">
                {activeSlides.map((slide, slideIndex) => (
                  <button
                    aria-current={slideIndex === index ? "true" : undefined}
                    aria-label={`Показать слайд ${slideIndex + 1}`}
                    className={slideIndex === index ? "is-active" : ""}
                    key={slide.id}
                    onClick={() => setIndex(slideIndex)}
                    type="button"
                  />
                ))}
              </div>
            ) : null}
          </div>

          <div className="home-hero-cms-media">
            <img
              alt={current.imageAlt || current.title || "bitSTONE"}
              height={360}
              loading="eager"
              src={current.imageUrl || "/brand-logo.png"}
              width={460}
            />
          </div>
        </article>
      </div>
    </section>
  );
}
